import { useMemo, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "react-i18next";
import { Alert } from "reactstrap";
import { saveAs } from "file-saver";

import { convertToDateString, LoadingSpinner } from "../../../../constants/const";
import { encryptStorage } from "../../../../constants/EncryptStorage";
import { ApiFinance } from "../../../funds/ApiFinance";
import { GetLanguageString } from "../../../helper/Components";
import { GetSelectedLanguage, SelectOnlyDate } from "../../../helper/Method";
import { ShowToast } from "../../../shared/ShowToast";
import IncomeReportBalances from "./IncomeReportBalances";
import IncomeReportExportButtons from "./IncomeReportExportButtons";
import IncomeReportFilters from "./IncomeReportFilters";
import IncomeReportTable from "./IncomeReportTable";
import {
  fileNameFromDisposition,
  parseIncomeReportBlobError,
} from "./incomeReportDownload";
import { isIncomeReportUnitType } from "./incomeReportUnit";

const getInitialValues = () => {
  const loggedInUnitId = parseInt(encryptStorage.getItem("loggedInUnitId"));
  const unitTypeName = encryptStorage.getItem("unitTypeName");
  const tempDate = new Date();
  tempDate.setDate(1);
  return {
    unit: {
      value: loggedInUnitId,
      label: "",
      label1: unitTypeName,
    },
    excludeExUnit: true,
    startDate: SelectOnlyDate(tempDate),
    endDate: SelectOnlyDate(new Date()),
  };
};

const selectedUnitType = (unit) => unit?.label1 || unit?.lable1 || "";

const isInfaqRow = (row) =>
  Boolean(row?.isInfaq) ||
  (typeof row?.categoryCode === "string" &&
    row.categoryCode.toLowerCase().includes("infaq")) ||
  (typeof row?.category === "string" &&
    row.category.toLowerCase().includes("infaq"));

const IncomeReport = () => {
  const { t } = useTranslation();
  const [values, setValues] = useState(getInitialValues);
  const [loading, setLoading] = useState(false);
  const [searchTriggered, setSearchTriggered] = useState(false);
  const [unitLevelError, setUnitLevelError] = useState(false);
  const [onlyInfaq, setOnlyInfaq] = useState(false);
  const [onlyAbove5000, setOnlyAbove5000] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [report, setReport] = useState({
    cashInHand: null,
    amanat: null,
    transactions: [],
  });

  const filteredTransactions = useMemo(() => {
    let rows = report.transactions || [];
    if (onlyInfaq) {
      rows = rows.filter(isInfaqRow);
      if (onlyAbove5000) {
        rows = rows.filter((row) => Number(row.amount) >= 5000);
      }
    }
    return rows.map((row, index) => ({
      ...row,
      srNo: index + 1,
    }));
  }, [report.transactions, onlyInfaq, onlyAbove5000]);

  const handleSearch = (searchValues) => {
    if (loading) return;

    if (!isIncomeReportUnitType(selectedUnitType(searchValues.unit))) {
      setUnitLevelError(true);
      setSearchTriggered(false);
      setReport({ cashInHand: null, amanat: null, transactions: [] });
      return;
    }

    setUnitLevelError(false);
    setSearchTriggered(true);
    setLoading(true);

    ApiFinance()
      .GetIncomeReport({
        unit: {
          value: searchValues.unit.value,
          label: searchValues.unit.label || searchValues.unit.lable,
          label1: selectedUnitType(searchValues.unit),
        },
        unitId: searchValues.unit.value,
        startDate: convertToDateString(searchValues.startDate),
        endDate: convertToDateString(searchValues.endDate),
      })
      .then((res) => {
        setReport({
          cashInHand: res.data?.cashInHand || null,
          amanat: res.data?.amanat || null,
          transactions: res.data?.transactions || [],
        });
        setLoading(false);
      })
      .catch((err) => {
        const messageKey = err.response?.data?.message;
        toast.error(
          (messageKey && t(messageKey) !== messageKey ? t(messageKey) : null) ||
            messageKey ||
            t("income_report_load_failed")
        );
        setLoading(false);
      });
  };

  const buildExportPayload = () => ({
    unit: {
      value: values.unit.value,
      label: values.unit.label || values.unit.lable,
      label1: selectedUnitType(values.unit),
    },
    unitId: values.unit.value,
    startDate: convertToDateString(values.startDate),
    endDate: convertToDateString(values.endDate),
    onlyInfaq,
    onlyAbove5000,
    language: GetSelectedLanguage(),
  });

  const downloadReport = (apiCall, mimeType, fallbackName, loadingKey, successKey) => {
    if (downloading) return;

    const toastId = toast.loading(t(loadingKey));
    setDownloading(true);
    apiCall(buildExportPayload())
      .then((res) => {
        const blob = new Blob([res.data], { type: mimeType });
        saveAs(
          blob,
          fileNameFromDisposition(res.headers, fallbackName)
        );
        ShowToast(toastId, successKey, "success");
      })
      .catch(async (err) => {
        const messageKey = await parseIncomeReportBlobError(err);
        ShowToast(
          toastId,
          (messageKey && t(messageKey) !== messageKey ? messageKey : null) ||
            "income_report_download_failed",
          "error"
        );
      })
      .finally(() => setDownloading(false));
  };

  const handleExcel = () =>
    downloadReport(
      ApiFinance().GetIncomeReportExcel,
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "IncomeReport.xlsx",
      "downloading_excel",
      "downloaded_excel"
    );

  const handlePdf = () =>
    downloadReport(
      ApiFinance().GetIncomeReportPdf,
      "application/pdf",
      "IncomeReport.pdf",
      "downloading_pdf",
      "downloaded_pdf"
    );

  return (
    <div className="wrapper-custom wrapper wrapper-content animated fadeInRight">
      <ToastContainer
        autoClose={3000}
        bodyClassName={GetSelectedLanguage()}
        closeOnClick
        pauseOnHover={false}
        draggable
      />
      <div
        className="ibox"
        style={{
          borderRadius: "12px",
          overflow: "visible",
          position: "relative",
          zIndex: 20,
          boxShadow: "0 6px 20px rgba(45, 68, 85, 0.08)",
        }}
      >
        <div
          className="ibox-title"
          style={{
            background: "linear-gradient(135deg, #486A84 0%, #2D4455 100%)",
            color: "#fff",
            border: "none",
            borderRadius: "12px 12px 0 0",
          }}
        >
          <h5
            className={GetSelectedLanguage() === "ur" ? "ur" : ""}
            style={{ color: "#fff", margin: 0, fontWeight: 700 }}
          >
            <GetLanguageString props="income_report_title" />
          </h5>
        </div>
        <IncomeReportFilters
          values={values}
          setValues={setValues}
          onSubmit={handleSearch}
          loading={loading}
          onlyInfaq={onlyInfaq}
          setOnlyInfaq={setOnlyInfaq}
          onlyAbove5000={onlyAbove5000}
          setOnlyAbove5000={setOnlyAbove5000}
        />
      </div>
      {unitLevelError ? (
        <Alert color="warning">
          <GetLanguageString props="income_report_unit_level_required" />
        </Alert>
      ) : null}
      {searchTriggered ? (
        loading ? (
          <LoadingSpinner />
        ) : (
          <div
            className="ibox"
            style={{
              borderRadius: "12px",
              overflow: "visible",
              position: "relative",
              zIndex: 1,
              boxShadow: "0 6px 20px rgba(45, 68, 85, 0.08)",
            }}
          >
            <div className="ibox-content">
              <div className="d-flex justify-content-end m-b-sm">
                <IncomeReportExportButtons
                  onExcel={handleExcel}
                  onPdf={handlePdf}
                  disabled={downloading}
                />
              </div>
              <IncomeReportBalances
                cashInHand={report.cashInHand}
                amanat={report.amanat}
              />
              <IncomeReportTable transactions={filteredTransactions} />
            </div>
          </div>
        )
      ) : null}
    </div>
  );
};

export default IncomeReport;
